/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ChangeDetectorRef, Component, EventEmitter, HostListener, inject, Input, Output } from '@angular/core';
import { generateValidationErrorId, maskKeyId } from '@common/tech-shared';
import { FormControlEditorAbstractComponent, ValidationErrorComponent } from '@ods/component';
import { AttachmentComponent, AttachmentWrapperComponent, ButtonComponent, FileUploadButtonComponent } from '@ods/system';

@Component({
  selector: 'administration-key-file-upload',
  imports: [
    AttachmentComponent,
    AttachmentWrapperComponent,
    ButtonComponent,
    FileUploadButtonComponent,
    ValidationErrorComponent,
  ],
  templateUrl: './key-file-upload.component.html',
})
export class KeyFileUploadComponent extends FormControlEditorAbstractComponent {
  readonly _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  get effectiveButtonId(): string {
    return this.control?.name as string;
  }

  @Input() label: string;
  @Input() accept: string = 'application/json';
  @Input() dataTestId: string = 'key-file-upload';
  @Input() isRequired: boolean = true;
  @Input() deleteLabel: string;
  @Input() existingKeyId: string = '';

  @Output() readonly loadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public uploadedFileName: string = '';
  public loading: boolean = false;
  public readonly validationErrorId: string = generateValidationErrorId();

  public get caption(): string {
    if (this.uploadedFileName) return this.uploadedFileName;
    if (this.existingKeyId) return `Hochgeladener ${this.label}: ${maskKeyId(this.existingKeyId)}`;
    return '';
  }

  @HostListener('change', ['$event.target.files'])
  async onFilesUpload(files: FileList): Promise<void> {
    this.loading = true;
    this.loadingChange.emit(true);
    this._cdr.detectChanges();
    try {
      const text: string = await files[0].text();
      this.uploadedFileName = files[0].name;
      this.fieldControl.setValue(text);
    } finally {
      this.loading = false;
      this.loadingChange.emit(false);
    }
  }

  public deleteFile(): void {
    this.uploadedFileName = '';
    this.fieldControl.setValue(null);
    this.fieldControl.markAsDirty();
  }

  override handleFieldControlValueChange(value: unknown): void {
    this._cdr.detectChanges();
    this.onChange(value);
  }
}
